import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import AnalyzerPeriodSelector from "./AnalyzerPeriodSelector.jsx";
import AnalyzerFilterBar from "./AnalyzerFilterBar.jsx";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithDayjsDates } from "../../store/filterStore.js";
import { useAnalyzerScope, searchParamsToScope, scopeToSearchParams, splitFilters } from "./useAnalyzerScope";
import { useAnalyzerSpend, useAnalyzerDrivers, useAnalyzerAttribution, useAnalyzerAiSavings, usePerspectiveSeries, usePerspectiveTree, ownershipDriversFor, driversAtLevel, useBudgets, useBudgetSeries, useComparisonSpend, useForecastBreakdown } from "./useAnalyzerData";
import FilterDropdown from "../../components/ui-components/FilterDropdown";
import { dimensionById, labelForField, describeUsageType, filterFieldByValue } from "./analyzerDimensions";
import GroupingSelect from "./GroupingSelect.jsx";
import { budgetOptions } from "./BudgetSelect.jsx";
import BasisSelect, { ApiEquivalentToggle } from "./BasisSelect.jsx";
import AnalyzerChart, { shortLabel } from "./AnalyzerChart.jsx";
import BudgetBurnupChart, { firstBreachDay } from "./BudgetBurnupChart.jsx";
import MoversDriversPanel from "./MoversDriversPanel.jsx";
import DimensionAttributionPanel from "./DimensionAttributionPanel.jsx";
import AnalyzerKpiStrip from "./AnalyzerKpiStrip.jsx";
import InvestigateButton from "../investigations/components/InvestigateButton";
import FreshnessLabel from "../../components/ui-components/FreshnessLabel";
import { primaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import { humanEUR } from "../cost-control/forecast/format";

// LINK1 — the set of query keys that mark a URL as carrying a deep-linked
// analyzer scope. Checked once on mount so a normal /cost-analyzer visit
// (no params) is completely unaffected.
const SCOPE_PARAM_KEYS = ["from", "to", "dim", "budget", "filters", "owner"];

const Analyzer = () => {
	// Use Zustand store for filter state with dayjs dates
	const {
		startDate,
		endDate,
		setStartDate,
		setEndDate,
		filters,
		setFilters,
	} = useFilterStoreWithDayjsDates();

	const scope = useAnalyzerScope();
	const { keycloak } = useKeycloak();

	// LINK1/LINK2 — deep-link apply-on-mount, then two-way scope→URL sync.
	// Both live in ONE effect deliberately: two separate effects would race on
	// the very first commit — React doesn't re-render between two effects of
	// the same component in the same flush, so a second "sync" effect would
	// still see THIS render's pre-apply closure (stale scope/dates/filters)
	// and could clobber a fresh deep-link's URL before the apply-triggered
	// re-render caught up. Merging them makes that race impossible by
	// construction: the apply branch either runs (URL is the source, so the
	// store now mirrors it exactly — nothing to sync back this pass) or is
	// permanently skipped once `appliedScopeFromUrl` flips true, after which
	// every render always takes the sync branch with THIS render's real,
	// already-fresh values.
	//
	// Sync is diff-guarded against the URL's CURRENT analyzer-scope keys:
	// setSearchParams is only called when at least one of
	// from/to/dim/budget/filters actually differs from what
	// scopeToSearchParams produces for the live scope. That guard is what
	// prevents an update loop — setSearchParams triggers a re-render with a
	// new `searchParams` (a dependency here), which reruns the effect, but
	// that second run's diff is against the URL it just wrote, so it comes
	// back equal and stops. `{ replace: true }` means none of this spams
	// browser history, and a plain no-param visit ends up populating the URL
	// with the current (default) scope — acceptable, it's what makes the
	// analyzer shareable. Non-analyzer params already on the URL are
	// preserved: the merged URLSearchParams starts from the current one, only
	// the five scope keys are deleted/reset.
	const [searchParams, setSearchParams] = useSearchParams();
	const appliedScopeFromUrl = useRef(false);
	// StrictMode-safety: in dev StrictMode React runs setup→cleanup→setup
	// SYNCHRONOUSLY on mount with no re-render between, so after the apply branch
	// flips `appliedScopeFromUrl` the second synchronous setup would fall into the
	// sync branch while the closure still holds the STALE pre-apply scope — and
	// clobber a fresh deep-link URL. `justAppliedRef` makes the sync branch skip
	// exactly one run right after an apply, so it only ever syncs once the store
	// has actually re-rendered with the applied values (robust whether or not
	// StrictMode is enabled).
	const justAppliedRef = useRef(false);
	useEffect(() => {
		if (!appliedScopeFromUrl.current) {
			appliedScopeFromUrl.current = true;
			if (SCOPE_PARAM_KEYS.some((k) => searchParams.has(k))) {
				// At least one analyzer scope param is present, so the URL is
				// treated as the FULL authoritative scope — every scope field is
				// set from it (defaulting/clearing fields the URL omits), so a
				// deep-link can never be clobbered by stale values left in the
				// persisted store (e.g. a v1 investigation's total-scoped
				// "Explore in analyzer" link, which carries no `budget`, must not
				// reopen a previously-selected budget's burn-up view).
				const parsed = searchParamsToScope(searchParams);
				if (parsed.from) setStartDate(parsed.from);
				if (parsed.to) setEndDate(parsed.to);
				const dimId = (parsed.dim && dimensionById(parsed.dim)?.available) ? parsed.dim : "total";
				scope.setDimension(dimId);
				scope.setBudgetId(parsed.budget || "");
				// Task 8b: perspective filters ride a separate `owner` param
				// (parsed.ownerFilters), reconstructed into the store's
				// perspective-filter shape and merged alongside the flat
				// filters below — a no-`owner` deep-link is unaffected
				// (parsed.ownerFilters defaults to []).
				const flatFilters = parsed.filters.map((f) => ({ category: f.field, categoryDisplay: labelForField(f.field), selectedValues: f.values, values: [], key: f.field }));
				const perspectiveFilters = (parsed.ownerFilters || []).map((f) => ({
					category: f.dimensionCode,
					categoryDisplay: filterFieldByValue(f.dimensionCode)?.label,
					kind: "perspective",
					dimensionCode: f.dimensionCode,
					selectedValues: f.nodes.map((n) => String(n.id)),
					valueLabels: Object.fromEntries(f.nodes.map((n) => [String(n.id), n.label])),
					key: f.dimensionCode,
				}));
				setFilters([...flatFilters, ...perspectiveFilters]);
				justAppliedRef.current = true;
				return;
			}
			// No scope params: fall through to sync below with the current
			// (untouched, so still accurate) default scope.
		}

		// Skip exactly one sync pass right after an apply (see justAppliedRef note):
		// the store has just been set from the URL, so there is nothing to sync back,
		// and the closure may still be stale under StrictMode's synchronous re-setup.
		if (justAppliedRef.current) {
			justAppliedRef.current = false;
			return;
		}

		// Task 8b: split the store's filters into flat (unchanged `filters` wire)
		// and perspective (new `ownerFilters` wire, one entry per dimensionCode
		// with its selected nodes + labels) — mirrors the apply-on-mount split
		// above, in reverse.
		const flatOnly = (filters || []).filter((f) => f.kind !== "perspective");
		const ownerFilters = (filters || [])
			.filter((f) => f.kind === "perspective")
			.map((f) => ({
				dimensionCode: f.dimensionCode,
				nodes: (f.selectedValues || []).map((id) => ({ id, label: f.valueLabels?.[id] || id })),
			}));
		const next = scopeToSearchParams({
			from: startDate.format("YYYY-MM-DD"),
			to: endDate.format("YYYY-MM-DD"),
			dim: scope.dimension.id,
			budget: scope.budgetId,
			filters: flatOnly.map((f) => ({ field: f.category, values: f.selectedValues || [] })),
			ownerFilters,
		});
		const changed = SCOPE_PARAM_KEYS.some((k) => (searchParams.get(k) || "") !== (next[k] || ""));
		if (!changed) return;
		const merged = new URLSearchParams(searchParams);
		SCOPE_PARAM_KEYS.forEach((k) => merged.delete(k));
		Object.entries(next).forEach(([k, v]) => merged.set(k, v));
		setSearchParams(merged, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams, setSearchParams, startDate, endDate, scope.dimension.id, scope.budgetId, filters]);
	const isPerspective = scope.dimension.kind === "perspective";
	// Budget mode (BUD3): a selected budget swaps the main chart for its
	// cumulative burn-up vs limit instead of the spend AnalyzerChart. All
	// hooks below still run unconditionally every render (React's rules of
	// hooks) — only the JSX branches on budgetMode.
	const budgetMode = scope.budgetId !== "";
	const budgets = useBudgets(keycloak.token);
	const budgetSeriesQ = useBudgetSeries(scope.budgetId, scope.period, keycloak.token);

	// Flat/total dims source spend from /get-cloud-cost as before; perspectives
	// source their stacked per-owner-node spend from /analyzer/perspective-series,
	// scoped to the current drill level (scope.perspectiveParentId).
	const flatSpend = useAnalyzerSpend(scope, keycloak.token, !isPerspective);
	const perspectiveSpend = usePerspectiveSeries({
		dimensionCode: scope.dimension.dimensionCode,
		parentOwnerNodeId: scope.perspectiveParentId,
		startDate: startDate.format("YYYY-MM-DD"),
		endDate: endDate.format("YYYY-MM-DD"),
	}, keycloak.token);
	const spendDetails = isPerspective ? (perspectiveSpend.data || []) : (flatSpend.data?.details || []);
	// Data-freshness label ("Data through 19 Jul") — the latest COMPLETE day the
	// loaded spend series has data for. Today is still in progress, so the current
	// day (and anything later) is excluded; the cut-off is the last day < today.
	// Formatted "D MMM"; null if there's no data.
	const dataThrough = (() => {
		const now = new Date();
		const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
		let max = null;
		for (const r of spendDetails) {
			const t = r?.time ? String(r.time).slice(0, 10) : null;
			if (!t || t >= todayIso) continue; // skip the in-progress day (and any future point)
			if (!max || t > max) max = t;
		}
		if (!max) return null;
		const d = new Date(`${max}T00:00:00`);
		return isNaN(d.getTime()) ? null : `${d.getDate()} ${d.toLocaleString("en-US", { month: "short" })}`;
	})();
	const shadowSeries = flatSpend.data?.shadow || [];
	const noConfirmedInvoices = flatSpend.data?.noConfirmedInvoices || false;
	const perspectiveEmpty = isPerspective && spendDetails.length === 0 && !perspectiveSpend.isLoading;

	// F3: AI actual-vs-shadow (API-equivalent) savings for the current window,
	// feeding the KPI strip's savings tile. Suppressed under owner scope (the
	// scoped tables carry no shadow/billing_mode — see BasisSelect note).
	const aiSavings = useAnalyzerAiSavings(startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"), keycloak.token).data;

	// The stacked chart's segments are keyed by node LABEL (grouping), not id
	// (perspective-series labels each row via nodeNames server-side). To drill
	// by clicking a segment we map the clicked label back to its node id via
	// the perspective tree, scoped to the current drill level (a label could in
	// principle repeat elsewhere in the tree). "Other" is a folded remainder
	// bucket (AnalyzerChart caps stacked series and folds the rest), not a real
	// node, so it is not drillable.
	const perspectiveTreeQ = usePerspectiveTree(isPerspective ? scope.dimension.dimensionCode : null, keycloak.token);
	const treeNodes = perspectiveTreeQ.data || [];

	const driversQ = useAnalyzerDrivers(scope, keycloak.token);
	// B5/D5: in the non-budget Total state, the cross-dimension attribution
	// table replaces the SKU-based movers panel below. In budget mode (D5) the
	// same panel is shown, budget-scoped (buildAttributionRequest carries
	// scope.budgetId, and the backend resolves it to the rollup-backed
	// budget-scoped attribution). The hook is called unconditionally (rules of
	// hooks); `enabled` gates the actual fetch to only the states where the
	// panel is shown.
	const attributionQ = useAnalyzerAttribution(scope, keycloak.token, budgetMode || (!isPerspective && scope.dimension.id === "total"));

	// Comparison basis (period-over-period ghost line) — local view state. Drives the
	// PeriodComparisonChart when active + single-series. prior_period = the immediately
	// preceding equal-length window; prior_year = the same dates one year back. The hook
	// is called unconditionally (rules of hooks); `comparisonActive` gates the fetch.
	const [comparisonBasis, setComparisonBasis] = useState("none");
	const comparisonActive = comparisonBasis !== "none" && !budgetMode && !isPerspective;
	const compLen = endDate.diff(startDate, "day") + 1;
	const compFromD = comparisonBasis === "prior_year" ? startDate.subtract(1, "year") : startDate.subtract(compLen, "day");
	const compToD = comparisonBasis === "prior_year" ? endDate.subtract(1, "year") : endDate.subtract(compLen, "day");
	const comparisonLabel = comparisonBasis === "prior_year" ? "Same period last year" : "Prior period";
	// Bug fix: split filters like every other request builder does — cost-base
	// filters on `filters`, perspective/ownership filters on `ownerScopes` — so an
	// active perspective filter (e.g. organisation) scopes the comparison ghost
	// line instead of riding along as a bogus cost-base field and 400ing.
	// One split for the whole view: the comparison ghost and the forecast breakdown
	// must describe the same population as the actuals they sit against.
	const { costFilters, ownerScopes } = splitFilters(filters || []);
	// Single source of truth for "is any filter active", reusing the same split
	// every other request builder here uses — no second notion of "filtered".
	// Feeds AnalyzerChart's forward-total/band ruling: forecast_total_band_daily
	// is a whole-project total and cannot be filtered.
	const hasActiveFilters = costFilters.length > 0 || ownerScopes.length > 0;
	const comparisonSpendQ = useComparisonSpend(
		compFromD.format("YYYY-MM-DD"),
		compToD.format("YYYY-MM-DD"),
		costFilters,
		keycloak.token,
		comparisonActive,
		ownerScopes
	);
	// Perspective drivers come from the preview's ownership lens (decomposition.ownership),
	// keyed by owner-node id, rather than the flat cost-base driver breakdown.
	// ownershipDriversFor returns the WHOLE lens across every depth (per-node,
	// no rollup), but the stacked chart only shows the current drill level
	// (top-level nodes when no drill, else direct children of
	// scope.perspectiveParentId). driversAtLevel rolls each descendant driver UP
	// into its display-level ancestor and sums it there — the same subtree rollup
	// the chart and hierarchy do — so a parent whose cost lives on descendants
	// (0 direct delta) still shows its rolled-up mover instead of vanishing, and
	// the panel reconciles with the chart's segments. levelIds is null ONLY while
	// the tree hasn't loaded yet (treeNodes.length === 0, including the
	// non-perspective case where the tree query is disabled) — driversAtLevel then
	// falls back to the full list. Once loaded, levelIds is a real Set that may
	// legitimately be empty (e.g. a drilled leaf with no children), which rolls up
	// to no drivers. treeNodes supplies the parent pointers + level-node labels.
	const levelIds = treeNodes.length === 0
		? null
		: new Set(treeNodes.filter((n) => scope.perspectiveParentId == null ? n.parentId == null : n.parentId === scope.perspectiveParentId).map((n) => n.id));
	const drivers = isPerspective
		? driversAtLevel(ownershipDriversFor(driversQ.data?.decomposition, scope.dimension.dimensionCode), levelIds, treeNodes)
		: (driversQ.data?.drivers || []);

	// Perspective node drill: MoversDriversPanel stays dimension-agnostic (same
	// row shape for flat drivers and ownership rows) and always calls
	// onDrill(key, label); the branching between a flat-dim filter drill and a
	// perspective owner-node drill lives here.
	const handleDriverDrill = (key, label) => {
		if (isPerspective) scope.drillIntoNode(Number(key), label);
		else scope.drillInto(key);
	};

	const handleChartSegmentClick = (groupLabel) => {
		if (!isPerspective) { scope.drillInto(groupLabel); return; }
		if (groupLabel === "Other") return;
		const match = treeNodes.find((n) => n.label === groupLabel && (n.parentId ?? null) === scope.perspectiveParentId);
		if (match) scope.drillIntoNode(match.id, match.label);
	};

	// A perspective drilled to a single owner node scopes the forecast fan to
	// that node (owner_node scope, see useAnalyzerScope.buildPreviewSeed) — show
	// it as a single line + fan, same as a flat single-series drill. The
	// stacked (multi-node, non-drilled) level never gets a fan: it has no
	// single line to attach the projection to. scope.isSingleSeries stays
	// unconditionally false for perspectives (drives the drivers panel /
	// other consumers), so the chart's own isSingleSeries is computed here.
	const perspectiveSingleNode = isPerspective && scope.perspectiveParentId != null;
	const chartIsSingleSeries = perspectiveSingleNode ? true : scope.isSingleSeries;
	const chartForecast = (isPerspective && !perspectiveSingleNode) ? [] : (driversQ.data?.forecast?.series || []);
	// A forward projection only means anything against a period that reaches today.
	// Over a past-only window there is nothing for it to continue FROM, so the
	// forward stack would float free of the actuals it is meant to extend — hence
	// the breakdown fetch below is gated on it. The projection is always clipped to
	// the selected period (see mergeSingle), so a period ending today simply has no
	// room for one and the chart says so.
	const periodReachesToday = endDate.format("YYYY-MM-DD") >= new Date().toISOString().slice(0, 10);

	// Forward composition for the current breakdown. Total has its own fan already,
	// and budget mode swaps the chart entirely, so neither asks for this.
	const forecastBreakdownDim = isPerspective
		? scope.dimension.dimensionCode
		: scope.dimension.groupingField;
	// The filter split rides along: a filtered actual stack under an unfiltered
	// projection is a fabricated overrun at the boundary. The backend applies what
	// the forward tables carry and REFUSES the rest (source_key, perspective scopes,
	// any filter on an ownership breakdown) rather than answering unfiltered — that
	// refusal surfaces here as a failed query, so the chart simply keeps today's
	// actual-only behaviour instead of drawing a projection that ignores the chips.
	const forecastBreakdownQ = useForecastBreakdown(
		{
			from: startDate.format("YYYY-MM-DD"),
			to: endDate.add(90, "day").format("YYYY-MM-DD"),
			breakdown: forecastBreakdownDim,
			filters: costFilters,
			ownerScopes,
		},
		keycloak.token,
		!budgetMode && !chartIsSingleSeries && !!forecastBreakdownDim && periodReachesToday
	);

	return (
		<Fragment>
			{/* Investigate is the page's primary action — filled button in the row-1
			    actions cluster (between the compact Ask launcher and the avatar). */}
			<HeaderPortal slot="actions">
				<InvestigateButton seed={scope.buildPreviewSeed()} label="Investigate this" className={primaryButtonCls} />
			</HeaderPortal>

			{/* Period, comparison and budget: right-aligned context controls on row 2
			    (the tab strip — Cost & Usage Explorer / AI Usage Monitor — sits left). */}
			{/* Interactive controls read as one tight cluster (faint dividers between
			    them); the read-only freshness is set apart — smaller, muted, with a
			    clock icon and a "·" separator — so it doesn't look like a control. */}
			<HeaderPortal slot="row2-right">
				<div className="flex items-center gap-1">
					<AnalyzerPeriodSelector
						startDate={startDate}
						endDate={endDate}
						setStartDate={setStartDate}
						setEndDate={setEndDate}
						variant="tertiary"
					/>
					<span aria-hidden style={{ width: 1, height: 16 }} className="bg-card-bd" />
					<FilterDropdown
						value={comparisonBasis}
						onChange={setComparisonBasis}
						options={[
							{ value: "none", label: "No comparison" },
							{ value: "prior_period", label: "vs prior period" },
							{ value: "prior_year", label: "vs last year" },
						]}
						ariaLabel="Compare"
						minWidth={150}
						height={32}
						variant="tertiary"
					/>
					<span aria-hidden style={{ width: 1, height: 16 }} className="bg-card-bd" />
					<FilterDropdown
						value={scope.budgetId}
						onChange={scope.setBudgetId}
						options={budgetOptions(budgets)}
						ariaLabel="Budget"
						prefix="Budget:"
						minWidth={150}
						height={32}
						variant="tertiary"
					/>
					{dataThrough && (
						<>
							<span aria-hidden className="mx-1.5 text-[#C6CDD8] select-none">·</span>
							<FreshnessLabel>Data through {dataThrough}</FreshnessLabel>
						</>
					)}
				</div>
			</HeaderPortal>

			{/* Row 3: add filter (far left) + Breakdown + Basis, API-equivalent far
			    right (top); applied-filter chips (below). */}
			<HeaderPortal slot="row3-filters">
				<AnalyzerFilterBar
					filters={filters || []}
					setFilters={setFilters}
					token={keycloak.token}
					from={startDate.format("YYYY-MM-DD")}
					to={endDate.format("YYYY-MM-DD")}
					controls={
						<>
							{/* D5 v1: Breakdown is not meaningful while a budget scopes the
							    view (budget-scoped attribution ranks movers across ALL
							    dimensions itself) — hide it while budgetMode is on. */}
							{!budgetMode && (
								<GroupingSelect
									dimensionId={scope.dimension.id}
									onChange={scope.setDimension}
									drillPath={scope.drillPath}
									onDrillUp={scope.drillUp}
								/>
							)}
							<BasisSelect
								value={scope.basis}
								onChange={scope.setBasis}
								disabled={scope.isOwnerScoped}
							/>
						</>
					}
					trailing={
						<ApiEquivalentToggle
							showShadow={scope.showShadow}
							onShowShadowChange={scope.setShowShadow}
							disabled={scope.isOwnerScoped}
						/>
					}
				/>
			</HeaderPortal>

			{/* Content */}
			<div className="bg-backgroundgrey flex flex-col gap-4 p-6 overflow-y-auto">
				{/* Scope-aware headline KPIs — fed by the same decomposition/forecast the drivers
				    query already returns for the current scope (range + filters). Budget mode surfaces
				    projected-vs-cap in the burn-up, so the strip is non-budget only. */}
				{!budgetMode && (
					<AnalyzerKpiStrip
						totalSpend={driversQ.data?.decomposition?.comparisonCost || 0}
						priorSpend={driversQ.data?.decomposition?.baselineCost || 0}
						delta={driversQ.data?.deltaCost || 0}
						periodProjection={driversQ.data?.forecast?.period || null}
						aiSavings={scope.isOwnerScoped ? null : aiSavings}
					/>
				)}
				{budgetMode ? (
					<div className="rounded-card border border-card-bd bg-card p-4 flex flex-col gap-3">
						{(() => {
							const series = budgetSeriesQ.data?.series || [];
							const cap = budgetSeriesQ.data?.cap ?? null;
							const lastForecast = series.length > 0 ? series[series.length - 1].forecast_cum : null;
							const breachDay = firstBreachDay(series, cap);
							return (
								<Fragment>
									<div className="text-[13px] text-text-3">
										Projected {lastForecast != null ? humanEUR(lastForecast) : "—"} vs limit {cap != null ? humanEUR(cap) : "—"} · breach {breachDay || "—"}
									</div>
									<BudgetBurnupChart series={series} cap={cap} />
									<div className="text-[11px] text-text-3">
										Burn-up covers the budget's own period (month {scope.period}), which may differ from the analyzer date range above.
									</div>
								</Fragment>
							);
						})()}
					</div>
				) : (
					<div className="rounded-card border border-card-bd bg-card p-4">
						<AnalyzerChart
							isSingleSeries={chartIsSingleSeries}
							spendDetails={spendDetails}
							forecast={chartForecast}
							groupByLabel={scope.dimension.label || "cost source"}
							from={startDate.format("YYYY-MM-DD")}
							to={endDate.format("YYYY-MM-DD")}
							comparison={comparisonActive ? (comparisonSpendQ.data || []) : []}
							comparisonLabel={comparisonLabel}
							valueLabel={(v, max) => (scope.dimension.groupingField === "usage_type" ? describeUsageType(v) : shortLabel(v, max))}
							onAddFilter={handleChartSegmentClick}
							onZoom={(from, to) => { setStartDate(from); setEndDate(to); }}
							shadow={scope.isOwnerScoped ? [] : shadowSeries}
							forecastBreakdown={forecastBreakdownQ.data || null}
							hasActiveFilters={hasActiveFilters}
							interval={scope.resolvedInterval}
							cumulative={scope.cumulative}
							intervalChoice={scope.interval}
							onIntervalChange={scope.setInterval}
							onCumulativeChange={scope.setCumulative}
						/>
					</div>
				)}
				{!budgetMode && perspectiveEmpty && (
					<div className="text-[13px] text-text-3 px-1">No sub-nodes to show at this level.</div>
				)}
				{!budgetMode && scope.basis === "invoiced" && noConfirmedInvoices && (
					<div className="mt-1 text-[12px] text-text-3">No confirmed invoice yet for this period — the Invoiced view fills in once invoices are confirmed.</div>
				)}
				{budgetMode ? (
					<DimensionAttributionPanel
						dimensions={attributionQ.data?.dimensions || []}
						totalDelta={attributionQ.data?.totalDelta || 0}
						onPickDimension={(dim) => { scope.setBudgetId(""); scope.setDimension(dim); }}
					/>
				) : !isPerspective && scope.dimension.id === "total" ? (
					<DimensionAttributionPanel
						dimensions={attributionQ.data?.dimensions || []}
						totalDelta={attributionQ.data?.totalDelta || 0}
						onPickDimension={(dim) => scope.setDimension(dim)}
					/>
				) : (
					<MoversDriversPanel
						drivers={drivers}
						deltaCost={driversQ.data?.deltaCost || 0}
						onDrill={handleDriverDrill}
					/>
				)}
			</div>
		</Fragment>
	);
};

export default Analyzer;
